import useResponsive from '@/shared/hooks/useResponsive';

export type ProgressLineProps = {
	total?: number;
	answers?: boolean[];
	currentIndex?: number;
};

const MAX_LENGTH_MOBILE = 7;
const MAX_LENGTH_DESKTOP = 8;

// These stops mirror the corresponding marker offsets in lane-chooser-progress.scss.
const MOBILE_PROGRESS_STOPS = [16, 33, 52, 68, 85, 100] as const;

const getMobileProgressPercent = (total: number, answered: number): number => {
	const totalSteps = Math.max(0, Math.min(total, MAX_LENGTH_MOBILE));
	const answeredSteps = Math.max(0, Math.min(answered, totalSteps));

	if (totalSteps === 0 || answeredSteps === 0) {
		return 0;
	}

	const lastIndex = Math.min(totalSteps - 1, MOBILE_PROGRESS_STOPS.length - 1);
	const answeredIndex = Math.min(answeredSteps - 1, MOBILE_PROGRESS_STOPS.length - 1);
	const maxStop = MOBILE_PROGRESS_STOPS[lastIndex];

	return Math.min(MOBILE_PROGRESS_STOPS[answeredIndex], maxStop);
};

const getDesktopProgressPercent = (total: number, answered: number, maxLen: number): number => {
	const baseTotal = total + 1; // include the tent to keep the highlight flush with the end
	const totalVisibleSteps = Math.max(1, Math.min(baseTotal, maxLen));
	const answeredSteps = Math.min(answered + 1, totalVisibleSteps);
	const progress = Math.max(0, Math.min(1, answeredSteps / totalVisibleSteps));

	return progress * 100;
};

const PATH_MOBILE =
	'M40.9004 11.9107C40.9004 11.9107 30.9026 1.99999 21.588 1.99999C12.2734 1.99999 2.5 12 2.5 12M117.567 11.9107C117.567 11.9107 107.569 1.99999 98.2547 1.99999C88.9402 1.99999 79.1667 12 79.1667 12C79.1667 12 69.3932 22 60.0786 22C50.764 22 40.9906 12 40.9906 12M194.234 11.9107C194.234 11.9107 184.236 1.99999 174.921 1.99999C165.607 1.99999 155.833 12 155.833 12C155.833 12 146.06 22 136.745 22C127.431 22 117.657 12 117.657 12M232.5 12C232.5 12 222.727 22 213.412 22C204.097 22 194.324 12 194.324 12';

const PATH_TABLET =
	'M75.3374 16.875C75.3374 16.875 56.634 3 39.2088 3C21.7836 3 3.50001 17 3.50001 17M218.761 16.875C218.761 16.875 200.058 2.99999 182.633 2.99999C165.207 2.99999 146.924 17 146.924 17C146.924 17 128.64 31 111.215 31C93.7897 31 75.5061 17 75.5061 17M362.185 16.875C362.185 16.875 343.481 2.99999 326.056 2.99999C308.631 2.99999 290.348 17 290.348 17C290.348 17 272.064 31 254.639 31C237.214 31 218.93 17 218.93 17M433.771 17C433.771 17 415.488 31 398.062 31C380.637 31 362.354 17 362.354 17M505.914 16.875C505.914 16.875 487.21 2.99998 469.785 2.99998C452.36 2.99998 434.076 17 434.076 17M577.5 17C577.5 17 559.216 31 541.791 31C524.366 31 506.082 17 506.082 17';

const PATH_DESKTOP =
	'M120.643 16.875C120.643 16.875 90.0138 3 61.4777 3C32.9417 3 2.99996 17 2.99996 17M355.518 16.875C355.518 16.875 324.889 2.99999 296.353 2.99999C267.817 2.99999 237.875 17 237.875 17C237.875 17 207.933 31 179.397 31C150.861 31 120.919 17 120.919 17M590.393 16.875C590.393 16.875 559.764 2.99998 531.228 2.99998C502.692 2.99998 472.75 17 472.75 17C472.75 17 442.809 31 414.272 31C385.736 31 355.795 17 355.795 17M707.625 17C707.625 17 677.683 31 649.147 31C620.611 31 590.67 17 590.67 17M825.768 16.875C825.768 16.875 795.139 2.99997 766.603 2.99997C738.067 2.99997 708.125 17 708.125 17M943 17C943 17 913.058 31 884.522 31C855.986 31 826.044 17 826.044 17';

const ProgressLine = ({ total = 0, answers, currentIndex }: ProgressLineProps) => {
	const { isMobile, isTablet } = useResponsive();

	const totalQuestions = Math.max(0, total);
	const answeredCount = Math.max(0, answers?.length ?? 0);
	const maxLen = isMobile ? MAX_LENGTH_MOBILE : MAX_LENGTH_DESKTOP;

	const progressPercent = isMobile
		? getMobileProgressPercent(totalQuestions, answeredCount)
		: getDesktopProgressPercent(totalQuestions, answeredCount, maxLen);

	const highlightClip = Math.max(0, Math.min(100, 100 - progressPercent));
	const hasProgress = progressPercent > 0;
	const answeredForMarkers = Math.min(answeredCount, totalQuestions);
	const denom = Math.max(0, totalQuestions);

	const linePath = isMobile ? PATH_MOBILE : isTablet ? PATH_TABLET : PATH_DESKTOP;
	const svgHeight = isMobile ? 24 : 34;
	const svgWidth = isMobile ? 235 : isTablet ? 581 : 946;

	return (
		<div className="progress-line">
			<div className="progress-line__inner">
				<svg
					width={svgWidth}
					height={svgHeight}
					viewBox={`0 0 ${svgWidth} ${svgHeight}`}
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
					className="progress-line__default"
				>
					<path d={linePath} strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" />
				</svg>
				<svg
					width={svgWidth}
					height={svgHeight}
					viewBox={`0 0 ${svgWidth} ${svgHeight}`}
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
					aria-hidden={!hasProgress}
					className="progress-line__highlight"
					style={{ clipPath: `inset(0 ${highlightClip}% 0 0)` }}
				>
					<path d={linePath} strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" />
				</svg>
			</div>

			<div className="progress-line__markers" aria-hidden={denom === 0}>
				{Array.from({ length: totalQuestions }).map((_, markerIndex) => {
					const answer = markerIndex < answeredForMarkers ? answers?.[markerIndex] ?? null : null;
					return (
						<div key={markerIndex} className="progress-line__marker">
							{currentIndex === markerIndex ? (
								<span className="icon icon-arrow-pointer" />
							) : answer === true ? (
								<span className="icon icon-correct-green" />
							) : answer === false ? (
								<span className="icon icon-incorrect-red" />
							) : (
								<span className="empty-circle" />
							)}
						</div>
					);
				})}
			</div>

			<div className="progress-line__end" />
		</div>
	);
};

export default ProgressLine;
